// components/SetupWizard.jsx
// Step-by-step guided setup wizard modal.
// Triggered from SetupChecklist compact bar. Skippable at any step.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'https://brokerconnect.co.uk/api';

const STEP_META = {
  company_profile: {
    title: 'Company Profile',
    description: 'Add your company name and upload your logo. This appears on introducer agreements, client portals, and emails sent on your behalf.',
    action: 'Go to Company Profile',
    route: '/settings',
    icon: '🏢',
  },
  agreement_uploaded: {
    title: 'Introducer Agreement',
    description: "Upload your network's introducer agreement template. BrokerConnect will parse it so introducers can sign digitally — no printing or scanning needed.",
    action: 'Go to Agreements',
    route: '/owner/agreements',
    icon: '📄',
  },
  team_added: {
    title: 'Add a Team Member',
    description: 'Invite a manager or adviser to join your company. They can then be assigned deals and manage introductions.',
    action: 'Go to Team',
    route: '/owner/users',
    icon: '👥',
  },
  first_invite: {
    title: 'Invite Your First Introducer',
    description: 'Send an introducer invite link. Once they sign your agreement, they can start referring clients directly into your pipeline.',
    action: 'Go to Introducers',
    route: '/introducers',
    icon: '🤝',
  },
};

export default function SetupWizard({ token, onClose }) {
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [stepIdx, setStepIdx] = useState(0);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res  = await fetch(`${API}/agreement-templates/setup-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setStatus(data);
      // Start on first incomplete step
      const keys = Object.keys(data.items);
      const firstIncomplete = keys.findIndex(k => !data.items[k].done);
      setStepIdx(firstIncomplete >= 0 ? firstIncomplete : 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading || !status) return null;

  const stepKeys   = Object.keys(status.items);
  const totalSteps = stepKeys.length;
  const currentKey = stepKeys[stepIdx];
  const currentItem = status.items[currentKey];
  const meta       = STEP_META[currentKey] || {};
  const doneCount  = Object.values(status.items).filter(i => i.done).length;
  const allDone    = doneCount === totalSteps;

  const goNext = () => {
    if (stepIdx < totalSteps - 1) setStepIdx(stepIdx + 1);
    else onClose();
  };
  const goPrev = () => { if (stepIdx > 0) setStepIdx(stepIdx - 1); };

  const handleAction = () => {
    onClose();
    navigate(meta.route);
  };

  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modal}>

        {/* Header */}
        <div style={header}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#10b981', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>
              Setup Wizard
            </div>
            <div style={{ fontSize: 13, color: '#718096' }}>
              {doneCount} of {totalSteps} steps complete
            </div>
          </div>
          <button onClick={onClose} style={closeBtn} title="Close">✕</button>
        </div>

        {/* Progress bar */}
        <div style={{ height: 4, background: '#EDF2F7', margin: '0 0 28px' }}>
          <div style={{
            height: '100%',
            width: `${(doneCount / totalSteps) * 100}%`,
            background: 'linear-gradient(90deg, #10b981, #84cc16)',
            transition: 'width 0.4s ease',
          }} />
        </div>

        {/* Step dots */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 28 }}>
          {stepKeys.map((key, i) => {
            const done = status.items[key].done;
            const active = i === stepIdx;
            return (
              <button
                key={key}
                onClick={() => setStepIdx(i)}
                title={STEP_META[key]?.title || key}
                style={{
                  width: active ? 24 : 10,
                  height: 10,
                  borderRadius: 5,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: done ? '#10b981' : active ? '#1e40af' : '#CBD5E0',
                  padding: 0,
                }}
              />
            );
          })}
        </div>

        {/* Step content */}
        {allDone ? (
          <div style={{ textAlign: 'center', padding: '20px 0 28px' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0e1b2c', margin: '0 0 8px' }}>
              You're all set!
            </h2>
            <p style={{ fontSize: 14, color: '#718096', margin: '0 0 24px' }}>
              Your workspace is fully configured. You won't see this wizard again unless new setup steps are added.
            </p>
            <button onClick={onClose} style={btnPrimary}>Start using BrokerConnect →</button>
          </div>
        ) : (
          <div style={{ padding: '0 4px' }}>
            {/* Icon + title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
              <div style={{
                width: 52, height: 52, borderRadius: 12, flexShrink: 0,
                background: currentItem.done ? '#f0fdf4' : '#eff6ff',
                border: `2px solid ${currentItem.done ? '#bbf7d0' : '#bfdbfe'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
              }}>
                {currentItem.done ? '✅' : meta.icon}
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                  Step {stepIdx + 1} of {totalSteps}
                </div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0e1b2c', margin: 0 }}>
                  {meta.title}
                </h2>
              </div>
            </div>

            {currentItem.done ? (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '14px 16px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 16 }}>✓</span>
                <span style={{ fontSize: 14, color: '#166534', fontWeight: 500 }}>This step is complete</span>
              </div>
            ) : (
              <p style={{ fontSize: 14, color: '#4a5568', lineHeight: 1.65, margin: '0 0 24px' }}>
                {meta.description}
              </p>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {!currentItem.done && (
                <button onClick={handleAction} style={btnPrimary}>
                  {meta.action} →
                </button>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                {stepIdx > 0 && (
                  <button onClick={goPrev} style={{ ...btnSecondary, flex: 1 }}>← Back</button>
                )}
                {stepIdx < totalSteps - 1 ? (
                  <button onClick={goNext} style={{ ...btnSecondary, flex: 1 }}>
                    {currentItem.done ? 'Next →' : 'Skip for now'}
                  </button>
                ) : (
                  <button onClick={onClose} style={{ ...btnSecondary, flex: 1 }}>
                    Done for now
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Outstanding summary */}
        {!allDone && (
          <div style={{ marginTop: 28, borderTop: '1px solid #EDF2F7', paddingTop: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
              Outstanding
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {stepKeys.filter(k => !status.items[k].done).map(k => (
                <div
                  key={k}
                  onClick={() => setStepIdx(stepKeys.indexOf(k))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', borderRadius: 8,
                    background: currentKey === k ? '#eff6ff' : '#f7fafc',
                    border: `1px solid ${currentKey === k ? '#bfdbfe' : '#e2e8f0'}`,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: currentKey === k ? '#1e40af' : '#CBD5E0', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#2d3748', fontWeight: currentKey === k ? 600 : 400 }}>
                    {STEP_META[k]?.title || k}
                  </span>
                  <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 'auto' }}>
                    {status.items[k].hint}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const overlay = {
  position: 'fixed', inset: 0, zIndex: 1000,
  background: 'rgba(14, 27, 44, 0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 20,
};

const modal = {
  background: '#fff',
  borderRadius: 16,
  padding: '28px 28px 24px',
  width: '100%',
  maxWidth: 520,
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
};

const header = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: 20,
};

const closeBtn = {
  background: '#f3f4f6',
  border: 'none',
  borderRadius: 8,
  width: 32,
  height: 32,
  cursor: 'pointer',
  fontSize: 14,
  color: '#6b7280',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const btnPrimary = {
  background: '#1e40af',
  color: '#fff',
  border: 'none',
  borderRadius: 10,
  padding: '12px 20px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  width: '100%',
  textAlign: 'center',
};

const btnSecondary = {
  background: '#f3f4f6',
  color: '#374151',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  padding: '11px 20px',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  textAlign: 'center',
};
